import React, {useState, useRef, useEffect, useMemo} from 'react';
import { useSpring, animated, config, useTransition, to } from 'react-spring';
import { Sun, Moon, Star, Sunrise, Sunset, Coffee, Users, Zap, Laptop, Home, Rocket, Satellite, Earth, Atom } from 'lucide-react';

// Need to use string literals for the bgGradient property, otherwise animated.div won't animate the gradient
const timelineEvents = [
    {
        time: '6:00 AM',
        icon: Sunrise,
        title: 'Rise and Shine!',
        description: 'CosmOS wakes up, checking weather and energy prices.',
        content: "While you're still hitting the snooze button, CosmOS is wide awake. It's checking the weather forecast, energy prices, and even the local grid's carbon intensity. Based on this info, it's already planning the most efficient way to heat the building for the day ahead.",
        bgGradient: ['#312e81', '#fdba74', '#60a5fa'],
        darkOverlay: 0.3,
        sunPosition: 10,
        moonPosition: 10,
        numStars: 30
    },
    {
        time: '8:30 AM',
        icon: Users,
        title: 'Early Birds Arrive',
        description: 'Lights and HVAC activate only where needed.',
        content: "As the first employees swipe their access cards, CosmOS springs into action. Lights come on, but only where needed. The HVAC system kicks in for occupied areas. And if someone forgot to pay their desk rental? Well, their access card might just decide it's not in the mood to work today.",
        bgGradient: ['#60a5fa', '#fef08a', '#3b82f6'],
        darkOverlay: 0,
        sunPosition: 30,
        moonPosition: -50,
        numStars: 10
    },
    {
        time: '10:00 AM',
        icon: Zap,
        title: 'Solar Power Party',
        description: 'Excess solar power diverted to heat water and charge batteries.',
        content: "The sun's beaming, and your roof is having a photon feast. CosmOS spots the surplus solar power and gets clever. Instead of returning the excess to the grid, it channels it into heating water for the afternoon tea break and giving the batteries a boost. Now that's what we call a bright idea!",
        bgGradient: ['#3b82f6', '#fde047', '#60a5fa'],
        darkOverlay: 0,
        sunPosition: 50,
        moonPosition: -80,
        numStars: 5
    },
    {
        time: '12:00 PM',
        icon: Coffee,
        title: 'Lunchtime Lowdown',
        description: 'CosmOS runs diagnostics during quiet time.',
        content: "While everyone's out for lunch, CosmOS takes advantage of the quiet time. It runs a quick diagnostic on all systems, identifying a slight inefficiency in one of the AC units. It schedules a maintenance check before it becomes a problem. Preventive maintenance for the win!",
        bgGradient: ['#60a5fa', '#facc15', '#93c5fd'],
        darkOverlay: 0,
        sunPosition: 70,
        moonPosition: -100,
        numStars: 5
    },
    {
        time: '2:00 PM',
        icon: Laptop,
        title: 'Meeting Madness',
        description: 'Last-minute meeting? No problem! Room perfectly prepared.',
        content: "A last-minute meeting is scheduled in the conference room. No problem! CosmOS adjusts the HVAC and lighting for the room, ensuring it's comfortable when attendees arrive. No more walking into freezing cold meeting rooms!",
        bgGradient: ['#93c5fd', '#fde047', '#fb923c'],
        darkOverlay: 0,
        sunPosition: 80,
        moonPosition: -80,
        numStars: 10
    },
    {
        time: '5:00 PM',
        icon: Home,
        title: 'Home Time Hustle',
        description: 'Systems power down, except for late workers.',
        content: "As people start to leave, CosmOS gradually powers down unnecessary systems. But wait! The marketing team is working late on a big project. CosmOS keeps their area comfortable while saving energy everywhere else.",
        bgGradient: ['#fb923c', '#fca5a5', '#3730a3'],
        darkOverlay: 0.2,
        sunPosition: 100,
        moonPosition: -30,
        numStars: 30
    },
    {
        time: '9:00 PM',
        icon: Sunset,
        title: 'Nighttime Ninja',
        description: 'Preparing reports and charging EVs.',
        content: "The building's quiet, but CosmOS is still hard at work. It's crunching the day's data, preparing reports on energy usage and carbon emissions. It's also taking advantage of lower nighttime energy rates to charge any electric vehicle stations, ready for the morning commute.",
        bgGradient: ['#3730a3', '#581c87', '#1e1b4b'],
        darkOverlay: 0.6,
        sunPosition: 120,
        moonPosition: 10,
        numStars: 60
    },
    {
        time: '11:59 PM',
        icon: Moon,
        title: 'Midnight Musings',
        description: 'CosmOS reflects on a day well managed.',
        content: "As the clock ticks towards midnight, CosmOS reflects on the day (okay, it doesn't really reflect, but let's pretend!). It's saved energy, reduced carbon emissions, kept everyone comfortable, and even preempted a maintenance issue. Not bad for a day's work!",
        bgGradient: ['#1e1b4b', '#581c87', '#312e81'],
        darkOverlay: 0.8,
        sunPosition: 130,
        moonPosition: 80,
        numStars: 80
    },
];

const connectingIcons = [Rocket, Satellite, Earth, Atom];

const AnimatedStar = animated(Star);

const CosmosTimeline = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const sectionRefs = useRef([]);

    const [{ scrollY }, setScrollY] = useSpring(() => ({ scrollY: 0 }));

    const onScroll = () => {
        setScrollY({ scrollY: window.scrollY });
    };

    useEffect(() => {
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = sectionRefs.current.findIndex((ref) => ref === entry.target);
                        setCurrentIndex(index);
                    }
                });
            },
            { threshold: 0.5 }
        );

        sectionRefs.current.forEach((ref) => observer.observe(ref));

        return () => observer.disconnect();
    }, []);

    const backgroundStyle = useSpring({
        background: `linear-gradient(to bottom, ${timelineEvents[currentIndex].bgGradient.join(', ')})`,
        config: { duration: 1000 }
    });

    const darkOverlayStyle = useSpring({
        opacity: timelineEvents[currentIndex].darkOverlay,
        config: { duration: 1000 }
    });

    const sunStyle = useSpring({
        bottom: `${timelineEvents[currentIndex].sunPosition}%`,
        transform: scrollY.to(
            y => `rotate(${y / 30}deg)`
        ),
        config: { duration: 1000 }
    });

    const moonStyle = useSpring({
        top: `${timelineEvents[currentIndex].moonPosition}%`,
        config: { duration: 1000 }
    });

    const stars = useMemo(() =>
            Array.from({ length: Math.max(...timelineEvents.map(e => e.numStars)) }, (_, i) => ({
                id: i,
                x: Math.random(),
                y: Math.random(),
                size: Math.random() * 15 + 5,
                parallaxFactor: Math.random() * 0.5 + 0.5,
            })),
        []
    );

    const starsTransition = useTransition(
        stars.slice(0, timelineEvents[currentIndex].numStars),
        {
            from: { opacity: 0 },
            enter: { opacity: 1 },
            leave: { opacity: 0 },
            trail: 100,
            config: { tension: 300, friction: 20 },
        }
    );

    return (
        <div className="relative bg-gray-900 text-white">
            <animated.div className="fixed inset-0 z-0 transition-colors duration-1000" style={backgroundStyle} />
            <animated.div className="fixed inset-0 bg-black z-10" style={darkOverlayStyle} />
            <div className="fixed inset-0 z-20 overflow-hidden pointer-events-none">
                <animated.div
                    style={{
                        ...sunStyle,
                        left: '10%',
                    }}
                    className="absolute text-yellow-300 transition-opacity duration-1000"
                >
                    <Sun size={100} />
                </animated.div>
                <animated.div
                    style={{
                        ...moonStyle,
                        right: '10%',
                    }}
                    className="absolute text-gray-300 transition-opacity duration-1000"
                >
                    <Moon size={80} />
                </animated.div>
                {starsTransition((style, star) => (
                    <AnimatedStar
                        key={star.id}
                        style={{
                            ...style,
                            position: 'absolute',
                            left: to(
                                [scrollY],
                                (y) => {
                                    const t = (y * star.parallaxFactor) % 3000 / 3000;
                                    return `${((star.x + t) % 1) * 100}%`;
                                }
                            ),
                            top: to(
                                [scrollY],
                                (y) => {
                                    const t = (y * star.parallaxFactor) % 3000 / 3000;
                                    return `${((star.y - t + 1) % 1) * 100}%`;
                                }
                            ),
                        }}
                        size={star.size}
                        className="text-white"
                    />
                ))}
            </div>
            <div className="relative z-30">
                {timelineEvents.map((event, index) => (
                    <React.Fragment key={index}>
                        <div
                            ref={el => sectionRefs.current[index] = el}
                            className="min-h-screen flex flex-col justify-center px-10 py-20"
                        >
                            <div className="bg-gray-900 bg-opacity-70 p-8 rounded-lg transition-all duration-500 backdrop-blur-sm">
                                <h2 className="text-4xl font-bold text-purple-300 mb-4">{event.time}</h2>
                                <div className="flex items-center mb-4">
                                    <event.icon className="mr-4 text-yellow-300" size={36} />
                                    <h3 className="text-3xl font-semibold text-white">{event.title}</h3>
                                </div>
                                <p className="text-xl text-gray-300 mb-6">{event.description}</p>
                                <p className="text-lg text-white">{event.content}</p>
                            </div>
                        </div>
                        {index < timelineEvents.length - 1 && (
                            <div className="flex justify-center items-center h-32">
                                <div className="w-1 bg-yellow-300 h-full"></div>
                                <div className="absolute bg-gray-900 rounded-full p-2">
                                    {React.createElement(connectingIcons[index % connectingIcons.length], { size: 24, className: "text-yellow-300" })}
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default CosmosTimeline;
